import type { MailAmountRow, MailBlock } from '@data/utils/types';

// innerText keeps a typed line break as one; textContent is the fallback where there is no layout.
const textOf = (node: HTMLElement): string => node.innerText ?? node.textContent ?? '';

const cellsOf = (row: HTMLTableRowElement): MailAmountRow => {
  const [label, amount] = Array.from(row.cells).map(cell => textOf(cell).trim());
  return { label: label ?? '', amount: amount ?? '' };
};

const filled = (rows: MailAmountRow[]): MailAmountRow[] => rows.filter(row => row.label || row.amount);

// By node type, not instanceof: the slot lives in the preview's frame, whose element classes are its own.
const blockOf = (node: ChildNode): MailBlock | null => {
  if (node.nodeType === Node.TEXT_NODE) return { kind: 'text', text: node.textContent?.trim() ?? '' };
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const element = node as HTMLElement;

  const table = element.tagName === 'TABLE' ? (element as HTMLTableElement) : element.querySelector('table');
  if (table) {
    const rows = Array.from(table.rows);
    return {
      kind: 'amounts',
      rows: filled(rows.filter(row => !row.hasAttribute('data-mail-total')).map(cellsOf)),
      totals: filled(rows.filter(row => row.hasAttribute('data-mail-total')).map(cellsOf)),
    };
  }
  const text = textOf(element)
    .replace(/[ \t]+$/gm, '')
    .trim();
  return { kind: element.hasAttribute('data-mail-heading') ? 'heading' : 'text', text };
};

const isFilled = (block: MailBlock | null): block is MailBlock => {
  if (!block) return false;
  return block.kind === 'amounts' ? block.rows.length + block.totals.length > 0 : !!block.text;
};

/** The edited mail body read back as the blocks the API rebuilds it from, whatever was emptied dropped. */
export const readMailBlocks = (slot: HTMLElement): MailBlock[] =>
  Array.from(slot.childNodes).map(blockOf).filter(isFilled);
